import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb, { getCategoryBreadcrumb } from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import CategoryTabs from '@/components/base/CategoryTabs';
import { comics, categories } from '@/mocks/comics';

const ITEMS_PER_PAGE = 8;

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState(1);

  const category = categories.find((c) => c.slug === slug);
  const categoryName = category?.name || '分类';

  const filteredComics = useMemo(() => {
    if (slug === 'recommended') {
      return [...comics].sort((a, b) => b.totalViews - a.totalViews);
    }
    return comics.filter((c) => c.categorySlug === slug);
  }, [slug]);

  const totalPages = Math.ceil(filteredComics.length / ITEMS_PER_PAGE);
  const pagedComics = filteredComics.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  const handleCategoryChange = (newSlug: string) => {
    setPage(1);
  };

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} - 韩国漫画网`,
    description: `韩国漫画网${categoryName}分类，提供最新最全的${categoryName}类韩国漫画资源，免费在线阅读，持续更新中。`,
    url: `https://www.koreanmanhwa.cn/category/${slug}`,
    hasPart: filteredComics.map((c) => ({
      '@type': 'CreativeWork',
      name: c.title,
      url: `https://www.koreanmanhwa.cn/article/${c.slug}`,
    })),
  };

  return (
    <>
      <SEOHead
        title={`${categoryName} - 韩国漫画网`}
        description={`韩国漫画网${categoryName}分类，提供最新最全的${categoryName}类韩国漫画资源。涵盖${categoryName}题材的精彩漫画，免费在线阅读，持续更新中。`}
        keywords={`${categoryName},韩国漫画,免费在线韩漫,${categoryName}漫画,精彩漫画资源`}
        canonicalUrl={`https://www.koreanmanhwa.cn/category/${slug}`}
        schema={schema}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="py-6">
            <Breadcrumb items={getCategoryBreadcrumb(slug || '')} />
          </div>

          <section className="mb-8" aria-label={`${categoryName} 分类导航`}>
            <CategoryTabs activeSlug={slug || 'recommended'} onCategoryChange={handleCategoryChange} />
          </section>

          <section aria-label={`${categoryName} 漫画列表`}>
            <div className="flex items-center gap-2 mb-6">
              <span className="w-7 h-7 flex items-center justify-center bg-primary-100 rounded-lg text-primary-600">
                <i className={category?.icon || 'ri-book-line'}></i>
              </span>
              <h1 className="font-heading text-2xl font-bold text-foreground-900">
                {categoryName}
              </h1>
              <span className="text-sm text-foreground-400 ml-1">
                共 {filteredComics.length} 部作品
              </span>
            </div>

            {filteredComics.length === 0 ? (
              <div className="text-center py-20">
                <span className="w-16 h-16 mx-auto flex items-center justify-center bg-background-200 rounded-full mb-4">
                  <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
                </span>
                <p className="text-foreground-500 text-lg">暂无该分类的漫画作品</p>
                <Link to="/category/recommended" className="inline-block mt-4 px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors">
                  去看看热门推荐
                </Link>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                  {pagedComics.map((comic) => (
                    <ComicCard key={comic.id} comic={comic} />
                  ))}
                </div>

                {totalPages > 1 && (
                  <nav className="flex items-center justify-center gap-2 mt-10 pb-10" aria-label="分页导航">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="w-10 h-10 flex items-center justify-center rounded-lg border border-background-300 text-foreground-600 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-left-s-line"></i>
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className={`w-10 h-10 flex items-center justify-center rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                          p === page
                            ? 'bg-primary-500 text-background-50'
                            : 'border border-background-300 text-foreground-600 hover:bg-background-100'
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                    <button
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="w-10 h-10 flex items-center justify-center rounded-lg border border-background-300 text-foreground-600 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                  </nav>
                )}
              </>
            )}
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}